import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { Wine, Briefcase, Cake, Trophy, Users, GraduationCap, Phone } from 'lucide-react'
import { events, siteData } from '../../data/siteData'

const iconMap: Record<string, React.ElementType> = {
  Wine, Briefcase, Cake, Trophy, Users, GraduationCap
}

export default function Events() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section id="events" className="relative py-24 sm:py-32 bg-section-dark grain-overlay" style={{ backgroundColor: 'rgba(10,14,10,0.55)', backdropFilter: 'blur(2px)' }}>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-military/40 to-transparent" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="font-mono text-xs text-military-light tracking-[0.4em] uppercase">
            Eventy
          </span>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl text-bone mt-4 tracking-wider">
            DLA KOGO?
          </h2>
          <p className="font-body text-chrome mt-4 max-w-2xl mx-auto">
            Powodów jest tyle, co magazynków.
          </p>
          <div className="w-24 h-0.5 bg-military-light mx-auto mt-6" />
        </motion.div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event, i) => {
            const Icon = iconMap[event.icon] || Users
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group relative p-6 bg-steel border-l-[3px] border-military rounded-sm hover:bg-steel-lighter hover:border-military-light transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-military/10 border border-military/30 rounded-sm group-hover:bg-military/20 transition-colors">
                    <Icon size={24} className="text-military-light" />
                  </div>
                  <div>
                    <h3 className="font-subheading font-bold text-lg text-bone mb-2 group-hover:text-military-light transition-colors">
                      {event.title}
                    </h3>
                    <p className="font-body text-sm text-chrome-light leading-relaxed">
                      {event.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <p className="font-body text-chrome mb-4">
            Masz pytania o konkretny event?
          </p>
          <a
            href={`tel:${siteData.phone}`}
            className="inline-flex items-center gap-2 px-8 py-3 border border-military-light/50 text-military-light font-subheading font-semibold text-sm uppercase tracking-widest rounded-sm hover:bg-military-light hover:text-bone transition-all duration-300"
          >
            <Phone size={16} />
            Zadzwoń do nas: {siteData.phone}
          </a>
        </motion.div>
      </div>
    </section>
  )
}
